/**
 * AddPermitModal Component
 * Form for creating/editing permits with all fields including V008 (due_date, recurring_period, fees_costs)
 * Phase 6 Batch 2 - Permits Frontend UI (Gold Standard Implementation)
 * Phase 9.16: Added dynamic custom fields from permit type definitions
 *
 * This component follows the recommended React Query pattern:
 * - Uses hooks from src/hooks/usePermits.ts (not callback props)
 * - Proper cache invalidation with prefix matching
 * - Testable with MSW (HTTP interception)
 *
 * @see docs/patterns/frontend/REACT_QUERY_PATTERNS.md
 * @see docs/OPTIMIZATION_ROADMAP.md - React Query Pattern Standardization
 */
import { useState, useEffect, useMemo } from 'react';
import { Permit, PermitCreate, PermitType, PERMIT_TYPES, RECURRING_PERIODS, FieldSchema } from '@/types/permits';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useCreatePermit, useUpdatePermit, useProjectScopes, usePermitTypes } from '@/hooks';
import { PermitPreviewPanel } from './PermitPreviewPanel';
import { DynamicFieldRenderer } from './DynamicFieldRenderer';

interface AddPermitModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  editingPermit?: Permit | null;
}

export function AddPermitModal({ isOpen, onClose, projectId, editingPermit }: AddPermitModalProps) {
  // Start with empty permit_type to force user selection (UX improvement 9.10)
  const [formData, setFormData] = useState<Partial<PermitCreate>>({});

  // Store fees_costs as string during user input, parse at submission
  // This matches AddItemModal pattern and prevents re-render issues during typing
  const [feesCostsInput, setFeesCostsInput] = useState<string>('');

  // 9.16b: State for custom field values (stored in custom_data JSONB)
  const [customData, setCustomData] = useState<Record<string, unknown>>({});

  // ✅ Use hooks from usePermits.ts
  const createPermit = useCreatePermit();
  const updatePermit = useUpdatePermit();
  const isSaving = createPermit.isPending || updatePermit.isPending;

  // 9.16b: Fetch permit type definitions for dropdown and field_schema
  const { data: permitTypesData } = usePermitTypes();
  const permitTypes = permitTypesData?.permit_types || [];

  // Find selected permit type's field_schema for dynamic fields
  const selectedPermitTypeDefinition = useMemo(() => {
    if (!formData.permit_type || !permitTypes.length) return null;
    return permitTypes.find(pt => pt.name === formData.permit_type) || null;
  }, [formData.permit_type, permitTypes]);

  // Derive whether the selected type is an internal milestone (no expiry/renewal)
  const isInternalMilestone = Boolean(selectedPermitTypeDefinition?.is_internal_milestone);

  // V052: Get project scopes to extract organizations for applicant selector (9.9e)
  const { data: scopesData } = useProjectScopes(projectId);

  // Extract unique organizations from scopes
  const organizations = useMemo(() => {
    if (!scopesData?.scopes) return [];

    const orgMap = new Map<string, { id: string; name: string }>();
    for (const scope of scopesData.scopes) {
      if (scope.organization_id && scope.organization_name) {
        orgMap.set(scope.organization_id, {
          id: scope.organization_id,
          name: scope.organization_name
        });
      }
    }
    return Array.from(orgMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [scopesData?.scopes]);

  // Reset/populate form when modal opens or editing permit changes
  useEffect(() => {
    if (editingPermit) {
      setFormData({
        permit_type: editingPermit.permit_type,
        case_number: editingPermit.case_number || undefined,
        permit_number: editingPermit.permit_number || undefined,
        reviewer_name: editingPermit.reviewer_name || undefined,
        date_submitted: editingPermit.date_submitted || undefined,
        date_issued: editingPermit.date_issued || undefined,
        date_closed: editingPermit.date_closed || undefined,
        due_date: editingPermit.due_date || undefined,
        recurring_period: editingPermit.recurring_period || undefined,
        notes: editingPermit.notes || undefined,
        applicant_organization_id: editingPermit.applicant_organization_id || undefined
      });
      // Populate fees_costs as string for input
      setFeesCostsInput(editingPermit.fees_costs?.toString() || '');
      // 9.16b: Populate custom_data from existing permit
      setCustomData((editingPermit as any).custom_data || {});
    } else {
      // Reset to empty for new permits (force user to select type)
      setFormData({});
      setFeesCostsInput('');
      setCustomData({});
    }
  }, [editingPermit, isOpen]);

  // 9.10 UX: Validation warnings
  const showRecurringWarning = formData.recurring_period && !formData.due_date;
  const isFormValid = Boolean(formData.permit_type);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.permit_type) {
      return; // Button should be disabled, but double-check
    }

    try {
      // Parse fees_costs from string to number at submission time
      // This matches AddItemModal pattern - keep as string during typing, parse once
      const fees_costs = feesCostsInput ? parseFloat(feesCostsInput) : undefined;

      // 9.16b: Include custom_data and permit_type_definition_id
      // Convert empty strings to undefined so backend receives null instead of ""
      // (Pydantic rejects "" for date fields, causing [object Object] toast errors)
      const cleanFormData = Object.fromEntries(
        Object.entries(formData).map(([k, v]) => [k, v === '' ? undefined : v])
      );
      const submitData = {
        ...cleanFormData,
        fees_costs,
        custom_data: Object.keys(customData).length > 0 ? customData : undefined,
        permit_type_definition_id: selectedPermitTypeDefinition?.id || undefined,
      } as PermitCreate;

      if (editingPermit) {
        await updatePermit.mutateAsync({ id: editingPermit.id, data: submitData });
      } else {
        await createPermit.mutateAsync({ projectId, permit: submitData });
      }
      onClose();
    } catch (error) {
      // Error handling is done in the hooks (toast notifications)
      console.error('Failed to save permit:', error);
    }
  };

  const updateField = (field: keyof PermitCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 9.16b: Handler for custom field changes
  const handleCustomFieldChange = (name: string, value: unknown) => {
    setCustomData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingPermit ? 'Edit Permit / Submission' : 'Add Permit / Submission'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Permit Type - Required field with placeholder (9.10 UX) */}
          <div>
            <Label htmlFor="permit_type">Permit Type <span className="text-red-500">*</span></Label>
            <Select
              value={formData.permit_type || ''}
              onValueChange={(value) => updateField('permit_type', value as PermitType)}
            >
              <SelectTrigger className={!formData.permit_type ? 'text-muted-foreground' : ''}>
                <SelectValue placeholder="Select permit type..." />
              </SelectTrigger>
              <SelectContent>
                {/* 9.16b: Use dynamic permit types from API with fallback to static list */}
                {(permitTypes.length > 0 ? permitTypes.map(pt => pt.name) : PERMIT_TYPES).map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Case Number */}
          <div>
            <Label htmlFor="case_number">Case Number</Label>
            <Input
              id="case_number"
              placeholder="e.g., DB-2024-12345"
              value={formData.case_number || ''}
              onChange={(e) => updateField('case_number', e.target.value || undefined)}
            />
          </div>

          {/* Permit Number */}
          <div>
            <Label htmlFor="permit_number">Permit Number</Label>
            <Input
              id="permit_number"
              placeholder="Issued by city"
              value={formData.permit_number || ''}
              onChange={(e) => updateField('permit_number', e.target.value || undefined)}
            />
          </div>

          {/* Reviewer Name */}
          <div>
            <Label htmlFor="reviewer_name">Reviewer Name</Label>
            <Input
              id="reviewer_name"
              value={formData.reviewer_name || ''}
              onChange={(e) => updateField('reviewer_name', e.target.value || undefined)}
            />
          </div>

          {/* Date Fields - with helper text explaining workflow (9.10 UX) */}
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              {isInternalMilestone
                ? <><strong>Milestone workflow:</strong> Submitted → Issued → Closed.</>
                : <><strong>Typical workflow:</strong> Submit → Issued → Closed. Set Due Date for recurring permits.</>
              }
            </p>
          </div>

          {/* Date Fields - Row 1 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_submitted">Date Submitted</Label>
              <Input
                id="date_submitted"
                type="date"
                value={formData.date_submitted || ''}
                onChange={(e) => updateField('date_submitted', e.target.value || null)}
              />
              <p className="text-xs text-muted-foreground mt-1">When sent to city</p>
            </div>

            <div>
              <Label htmlFor="date_issued">Date Issued</Label>
              <Input
                id="date_issued"
                type="date"
                value={formData.date_issued || ''}
                onChange={(e) => updateField('date_issued', e.target.value || null)}
              />
              <p className="text-xs text-muted-foreground mt-1">When approved by city</p>
            </div>
          </div>

          {/* Date Fields - Row 2 */}
          <div className={`grid gap-4 ${isInternalMilestone ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div>
              <Label htmlFor="date_closed">Date Closed</Label>
              <Input
                id="date_closed"
                type="date"
                value={formData.date_closed || ''}
                onChange={(e) => updateField('date_closed', e.target.value || null)}
              />
              <p className="text-xs text-muted-foreground mt-1">When work completed</p>
            </div>

            {/* Due date only shown for real permits (not internal milestones) */}
            {!isInternalMilestone && (
              <div>
                <Label htmlFor="due_date">Due Date / Expiration</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date || ''}
                  onChange={(e) => updateField('due_date', e.target.value || null)}
                />
                <p className="text-xs text-muted-foreground mt-1">When permit expires</p>
              </div>
            )}
          </div>

          {/* Recurring Period and Fees - only for real permits (not internal milestones) */}
          {!isInternalMilestone && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="recurring_period">Recurring Period</Label>
                <Select
                  value={formData.recurring_period || 'one-time'}
                  onValueChange={(value) => updateField('recurring_period', value === 'one-time' ? undefined : value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">One-time (no renewal)</SelectItem>
                    {RECURRING_PERIODS.map((period) => (
                      <SelectItem key={period} value={period}>
                        {period}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {showRecurringWarning ? (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Set a Due Date above for renewals to work
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave blank for one-time permits
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="fees_costs">Fees / Costs</Label>
                <Input
                  id="fees_costs"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={feesCostsInput}
                  onChange={(e) => setFeesCostsInput(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">Permit fees in dollars</p>
              </div>
            </div>
          )}

          {/* Who is handling this permit? (V052: 9.9e, renamed 9.10) */}
          <div>
            <Label htmlFor="applicant">Who is handling this permit?</Label>
            <Select
              value={formData.applicant_organization_id || 'self'}
              onValueChange={(value) => updateField('applicant_organization_id', value === 'self' ? null : value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="self">Our team (internal)</SelectItem>
                {organizations.length > 0 && (
                  <>
                    <SelectItem value="__divider__" disabled className="text-xs text-muted-foreground py-1">
                      — Client / External —
                    </SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              You'll only get reminders for permits your team is handling
            </p>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={4}
              value={formData.notes || ''}
              onChange={(e) => updateField('notes', e.target.value || undefined)}
            />
          </div>

          {/* 9.16b: Dynamic Custom Fields from permit type's field_schema */}
          <DynamicFieldRenderer
            fieldSchema={selectedPermitTypeDefinition?.field_schema}
            values={customData}
            onChange={handleCustomFieldChange}
          />

          {/* Preview Panel (9.10 UX) */}
          <PermitPreviewPanel
            formData={formData}
            applicantOrgName={
              formData.applicant_organization_id
                ? organizations.find(o => o.id === formData.applicant_organization_id)?.name
                : undefined
            }
            isRenewed={Boolean(editingPermit?.renewed_by_permit_id)}
          />

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !isFormValid}>
              {isSaving ? 'Saving...' : 'Save Permit'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
